import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogConfig,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import {
  NdaTemplateDocumentReferenceStore,
  TermSheetStore,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  AccessLevel,
  DocMetaResponse,
  DocumentAttribute,
  DocumentReferenceEntityType,
  DocumentType,
} from '@qtek/shared/models';
import { buildDocumentReferenceFormData } from '@qtek/shared/utils';
import { BehaviorSubject, filter, map, race, take, timer } from 'rxjs';
import {
  buildTermSheetReview,
  TermSheetExtractionResult,
} from './term-sheet-extraction.utils';

export const currentDealEntryMethodDialogConfig: MatDialogConfig = {
  panelClass: ['qt-dialog', 'current-deal-entry-method-dialog'],
  width: '600px',
  maxWidth: '95vw',
};

const TERM_SHEET_EXTRACTION_TIMER = 120000; // 120 seconds — term sheet extraction schema is ~48 fields; multi-facility docs (3+ facilities) can push 60–90s on Bedrock

@Component({
  selector: 'qt-current-deal-entry-method-dialog',
  templateUrl: './current-deal-entry-method-dialog.component.html',
  styleUrls: ['./current-deal-entry-method-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslationCoreModule,
  ],
})
export class CurrentDealEntryMethodDialogComponent implements OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private readonly ndaTemplateStore = inject(NdaTemplateDocumentReferenceStore);
  private termSheetStore = inject(TermSheetStore);
  private readonly docMeta$ = toObservable(this.termSheetStore.docMeta);
  private dialogRef = inject(
    MatDialogRef<CurrentDealEntryMethodDialogComponent>
  );
  private dialogLoader = inject(LazyDialogLoader);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  /** 'select' = card selection, 'upload' = file drop zone, 'processing' = extraction in progress */
  phase = signal<'select' | 'upload' | 'processing'>('select');
  dragCounter = 0;
  fileList: File[] = [];
  isUploading$ = new BehaviorSubject(false);
  currentUploadMessage$ = new BehaviorSubject('Uploading term sheet...');
  uploadError = signal(false);

  private termSheetMysid: string | null = null;
  private uploadMessageTimer: ReturnType<typeof setInterval> | null = null;
  /** Set to true when the dialog is closing — suppresses late extraction callbacks. */
  private cancelled = false;

  private uploadMessages = [
    'Uploading term sheet...',
    'Document uploaded successfully',
    'Reading term sheet...',
    'Extracting deal fields...',
  ];

  // ─── Card Actions ─────────────────────────────────────

  selectManualEntry(): void {
    this.cancelled = true;
    this.dialogRef.close();
    this.dialogLoader
      .open('create-item', { isLeadBank: true, entryMode: 'manual' })
      .subscribe();
  }

  selectUploadTermSheet(): void {
    this.phase.set('upload');
  }

  goBack(): void {
    const currentPhase = this.phase();
    if (currentPhase === 'upload') {
      this.cleanupUploadState();
      this.phase.set('select');
    } else if (currentPhase === 'select') {
      this.cancelled = true;
      this.dialogRef.close();
      this.dialogLoader.open('select-deal-type').subscribe();
    }
  }

  // ─── File Upload ──────────────────────────────────────

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.uploadError.set(false);
      this.fileList = Array.from(input.files);
      if (this.fileList.length > 0) {
        this.startUploadProcess();
      }
    }
  }

  handleDrop(event: DragEvent): void {
    this.dragCounter = 0;
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.uploadError.set(false);
      this.fileList = Array.from(files);
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
      if (this.fileList.length > 0) {
        this.startUploadProcess();
      }
    }
  }

  dragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter++;
  }

  dragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter--;
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
  }

  // ─── Upload & Extraction ──────────────────────────────

  private startUploadProcess(): void {
    if (this.fileList.length === 0) return;
    this.phase.set('processing');
    this.startUploadMessageCycling();
    this.uploadTermSheetDocument();
  }

  private uploadTermSheetDocument(): void {
    this.isUploading$.next(true);
    this.uploadError.set(false);

    const file = this.fileList[0];
    const formData = buildDocumentReferenceFormData({
      file,
      fileName: file.name,
      entityType: DocumentReferenceEntityType.NDA_TEMPLATE,
      // v2 equivalent of v1's `_ks: 'ndaTemplate={{<<crud-insert>>}}'` — create the parent
      // NdaTemplate as part of the upload.
      entityJson: '{}',
      attribute: DocumentAttribute.COMPANY_NDA,
      accessLevel: AccessLevel.SHARED,
      description: file.name,
      // Declared, not inferred: the user picked "term sheet" to get here, so the classifier
      // has nothing to add and its call stays off the stepper's timeout budget (SD-2745).
      documentType: DocumentType.TERM_SHEET,
    });

    this.ndaTemplateStore
      .upload(formData)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: reference => {
          // The document id is nested under the reference in v2.
          const documentId = reference?.document?._id;
          if (documentId) {
            this.subscribeToDocMeta(documentId);
          } else {
            this.handleUploadFailure();
          }
        },
        error: () => this.handleUploadFailure(),
      });
  }

  private subscribeToDocMeta(docId: string): void {
    this.termSheetMysid = `term_sheet_${docId}_${Date.now()}`;

    const mysid = this.termSheetMysid;

    this.termSheetStore.connect({ mysid, documentId: docId });

    const docMetaResponse$ = this.docMeta$.pipe(
      map(docMeta => docMeta[mysid]),
      filter(Boolean),
      take(1)
    );

    const timeout$ = timer(TERM_SHEET_EXTRACTION_TIMER);

    race(docMetaResponse$, timeout$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (this.cancelled) return;
        this.stopUploadMessageCycling();
        this.isUploading$.next(false);

        if (value && typeof value === 'object') {
          this.handleExtractionSuccess(value as DocMetaResponse, docId);
        } else {
          this.handleUploadFailure();
        }
      });
  }

  private handleExtractionSuccess(
    response: DocMetaResponse,
    docId: string
  ): void {
    if (this.cancelled) return;
    const containers = response?.containers;
    const data = containers?.[0]?.data as
      | Record<string, string | null>
      | undefined;

    if (!data || data['isValid'] !== 'true') {
      this.handleUploadFailure();
      return;
    }

    const subFacilityContainers =
      containers && containers.length > 1
        ? containers
            .slice(1)
            .map(c => c.data as Record<string, string | null>)
            .filter(Boolean)
        : undefined;

    const review = buildTermSheetReview(data, subFacilityContainers);
    if (review.fieldCount === 0 && review.subFacilities.length === 0) {
      this.handleUploadFailure();
      return;
    }

    this.openReviewDialog(review, docId);
  }

  private openReviewDialog(
    review: TermSheetExtractionResult,
    docId: string
  ): void {
    if (this.cancelled) return;
    this.cancelled = true;
    this.dialogRef.close();
    this.dialogLoader
      .open('term-sheet-review', {
        extraction: review,
        fileName: this.fileList[0]?.name ?? 'term-sheet.pdf',
        // SD-2150: pass the uploaded docId so the review dialog can link it to
        // the created deal (data room + covenant extraction discoverability).
        docId,
      })
      .subscribe();
  }

  private handleUploadFailure(): void {
    if (this.cancelled) return;
    this.stopUploadMessageCycling();
    this.isUploading$.next(false);
    this.uploadError.set(true);
    this.phase.set('upload');
    this.cdr.markForCheck();
  }

  // ─── Message Cycling ──────────────────────────────────

  private startUploadMessageCycling(): void {
    this.currentUploadMessage$.next(this.uploadMessages[0]);
    let currentIndex = 0;
    const interval = TERM_SHEET_EXTRACTION_TIMER / this.uploadMessages.length;

    this.uploadMessageTimer = setInterval(() => {
      currentIndex++;
      if (currentIndex < this.uploadMessages.length) {
        this.currentUploadMessage$.next(this.uploadMessages[currentIndex]);
      } else {
        this.stopUploadMessageCycling();
      }
    }, interval);
  }

  private stopUploadMessageCycling(): void {
    if (this.uploadMessageTimer) {
      clearInterval(this.uploadMessageTimer);
      this.uploadMessageTimer = null;
    }
  }

  // ─── Cleanup ──────────────────────────────────────────

  private cleanupUploadState(): void {
    this.stopUploadMessageCycling();
    this.fileList = [];
    this.isUploading$.next(false);
    this.uploadError.set(false);

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }

    if (this.termSheetMysid) {
      this.termSheetStore.disconnect(this.termSheetMysid);
      this.termSheetMysid = null;
    }
  }

  ngOnDestroy(): void {
    this.cancelled = true;
    this.cleanupUploadState();
    this.isUploading$.complete();
    this.currentUploadMessage$.complete();
  }
}
